'use client'

import {
  createContext,
  Dispatch,
  FC,
  memo,
  SetStateAction,
  useEffect,
  useState,
  useCallback,
  useMemo,
  useRef
} from 'react'
import { usePathname } from 'next/navigation'
import { useAtom } from 'jotai'
import { selectedToolsAtom } from '@/atoms/toolsAtoms'
import { updateMessageFeedback as updateMessageFeedbackInDB } from '@/db/messages'
import { Tables } from '@/supabase/types'
import { ChatMessage, ChatSettings } from '@/types'
import { safeConsole } from '@/lib/utils/safeConsole'

import { useRootMountMarker } from '@/components/utility/root-mount-marker'
import { MessageHtmlElement } from '@/types/html'
import Emitter from '@/lib/events/globalEventEmitter'

export interface ChatScopedState {
  chatMessages: ChatMessage[]
  chatFileItems: Tables<'file_items'>[]
  abortController: AbortController | null
  firstTokenReceived: boolean
  isGenerating: boolean
  isPersisting: boolean
  requestTokensTotal: number
  responseTimeToFirstToken: number
  responseTimeTotal: number
  responseTokensTotal: number
}

export interface ScopedChatBindings {
  getSnapshot: () => ChatScopedState
  setChatMessages: Dispatch<SetStateAction<ChatMessage[]>>
  setChatFileItems: Dispatch<SetStateAction<Tables<'file_items'>[]>>
  setAbortController: Dispatch<SetStateAction<AbortController | null>>
  setFirstTokenReceived: Dispatch<SetStateAction<boolean>>
  setIsGenerating: Dispatch<SetStateAction<boolean>>
  setIsPersisting: Dispatch<SetStateAction<boolean>>
  setRequestTokensTotal: Dispatch<SetStateAction<number>>
  setResponseTimeToFirstToken: Dispatch<SetStateAction<number>>
  setResponseTimeTotal: Dispatch<SetStateAction<number>>
  setResponseTokensTotal: Dispatch<SetStateAction<number>>
}

function createEmptyChatScopedState(): ChatScopedState {
  return {
    chatMessages: [],
    chatFileItems: [],
    abortController: null,
    firstTokenReceived: false,
    isGenerating: false,
    isPersisting: false,
    requestTokensTotal: 0,
    responseTimeToFirstToken: 0,
    responseTimeTotal: 0,
    responseTokensTotal: 0
  }
}

function resolveStateAction<Value>(
  value: SetStateAction<Value>,
  previous: Value
): Value {
  return typeof value === 'function'
    ? (value as (previous: Value) => Value)(previous)
    : value
}

interface ChatInputScopeContextValue {
  hasScopedInputState: boolean
  inputScopeId: string
}

/**
 * Combined input context kept for older callers. Prefer the split hooks in
 * use-scoped-user-input so typing does not re-render ChatUI / message lists.
 */
interface ChatInputStateContextValue extends ChatInputScopeContextValue {
  userInput: string
  setUserInput: Dispatch<SetStateAction<string>>
}

const ignoreUserInputUpdate: Dispatch<SetStateAction<string>> = () => {}
const chatInputStorageKeyPrefix = 'losi_chat_input:'
const chatInputStorageDelayMs = 500

const getChatInputStorageKey = (inputScopeId: string) =>
  `${chatInputStorageKeyPrefix}${inputScopeId}`

const readStoredChatInput = (inputScopeId: string) => {
  if (typeof window === 'undefined') {
    return ''
  }

  try {
    return localStorage.getItem(getChatInputStorageKey(inputScopeId)) || ''
  } catch {
    return ''
  }
}

const writeStoredChatInput = (inputScopeId: string, value: string) => {
  if (typeof window === 'undefined') {
    return
  }

  try {
    const storageKey = getChatInputStorageKey(inputScopeId)
    if (value.length > 0) {
      localStorage.setItem(storageKey, value)
    } else {
      localStorage.removeItem(storageKey)
    }
  } catch {}
}

export const ChatInputScopeContext = createContext<ChatInputScopeContextValue>({
  hasScopedInputState: false,
  inputScopeId: 'global'
})

export const ChatInputValueContext = createContext<string>('')

export const ChatInputSetContext =
  createContext<Dispatch<SetStateAction<string>>>(ignoreUserInputUpdate)

/** True only while the composer has non-whitespace text. Flips rarely. */
export const ChatComposerHasDraftContext = createContext(false)

export const ChatInputStateContext = createContext<ChatInputStateContextValue>({
  hasScopedInputState: false,
  inputScopeId: 'global',
  userInput: '',
  setUserInput: ignoreUserInputUpdate
})

/**
 * Keeps the chat tree from re-rendering on every keystroke. The input provider
 * owns draft state; without this memo, Provider re-renders would force ChatUI /
 * message lists to update even when they only need the setter / hasDraft flag.
 */
const ChatInputProviderChildren = memo(function ChatInputProviderChildren({
  children
}: {
  children: React.ReactNode
}) {
  return <>{children}</>
})

const ScopedChatInputStateProvider: FC<{
  children: React.ReactNode
  inputScopeId: string
}> = ({ children, inputScopeId }) => {
  const previousInputScopeIdRef = useRef(inputScopeId)
  const [userInput, setUserInput] = useState<string>(() =>
    readStoredChatInput(inputScopeId)
  )
  const hasComposerDraft = userInput.trim().length > 0
  const scopeValue = useMemo<ChatInputScopeContextValue>(
    () => ({
      hasScopedInputState: true,
      inputScopeId
    }),
    [inputScopeId]
  )
  // Legacy combined value — avoid using this from ChatUI / message list paths.
  const legacyValue = useMemo<ChatInputStateContextValue>(
    () => ({
      ...scopeValue,
      userInput,
      setUserInput
    }),
    [scopeValue, userInput]
  )

  useEffect(() => {
    if (previousInputScopeIdRef.current === inputScopeId) {
      return
    }

    previousInputScopeIdRef.current = inputScopeId
    setUserInput(readStoredChatInput(inputScopeId))
  }, [inputScopeId])

  useEffect(() => {
    if (userInput.length === 0) {
      writeStoredChatInput(inputScopeId, '')
      return
    }

    const saveTimer = setTimeout(() => {
      writeStoredChatInput(inputScopeId, userInput)
    }, chatInputStorageDelayMs)

    return () => clearTimeout(saveTimer)
  }, [inputScopeId, userInput])

  return (
    <ChatInputScopeContext.Provider value={scopeValue}>
      <ChatInputSetContext.Provider value={setUserInput}>
        <ChatComposerHasDraftContext.Provider value={hasComposerDraft}>
          <ChatInputValueContext.Provider value={userInput}>
            <ChatInputStateContext.Provider value={legacyValue}>
              <ChatInputProviderChildren>{children}</ChatInputProviderChildren>
            </ChatInputStateContext.Provider>
          </ChatInputValueContext.Provider>
        </ChatComposerHasDraftContext.Provider>
      </ChatInputSetContext.Provider>
    </ChatInputScopeContext.Provider>
  )
}

interface ChatbotUIChatContext {
  // PASSIVE CHAT STORE
  chatMessages: ChatMessage[]
  setChatMessages: Dispatch<SetStateAction<ChatMessage[]>>
  chatSettings: ChatSettings | null
  setChatSettings: Dispatch<SetStateAction<ChatSettings>>
  selectedChat: Tables<'chats'> | null
  setSelectedChat: Dispatch<SetStateAction<Tables<'chats'> | null>>
  chatFileItems: Tables<'file_items'>[]
  setChatFileItems: Dispatch<SetStateAction<Tables<'file_items'>[]>>

  // Chat input
  hasScopedInputState: boolean
  inputScopeId: string
  userInput: string
  setUserInput: Dispatch<SetStateAction<string>>

  // ACTIVE CHAT STORE
  abortController: AbortController | null
  setAbortController: Dispatch<SetStateAction<AbortController | null>>
  firstTokenReceived: boolean
  setFirstTokenReceived: Dispatch<SetStateAction<boolean>>
  isGenerating: boolean
  setIsGenerating: Dispatch<SetStateAction<boolean>>
  isPersisting: boolean
  setIsPersisting: Dispatch<SetStateAction<boolean>>
  isImageGenerating: boolean
  setIsImageGenerating: Dispatch<SetStateAction<boolean>>
  isMusicGenerating: boolean
  setIsMusicGenerating: Dispatch<SetStateAction<boolean>>
  isVideoGenerating: boolean
  setIsVideoGenerating: Dispatch<SetStateAction<boolean>>

  requestTokensTotal: number
  setRequestTokensTotal: Dispatch<SetStateAction<number>>
  responseTimeToFirstToken: number
  setResponseTimeToFirstToken: Dispatch<SetStateAction<number>>
  responseTimeTotal: number
  setResponseTimeTotal: Dispatch<SetStateAction<number>>
  responseTokensTotal: number
  setResponseTokensTotal: Dispatch<SetStateAction<number>>

  selectedTools: Tables<'tools'>[]
  setSelectedTools: Dispatch<SetStateAction<Tables<'tools'>[]>>
  getSelectedToolsSnapshot: () => Tables<'tools'>[]

  selectedHtmlElements: MessageHtmlElement[]
  setSelectedHtmlElements: Dispatch<SetStateAction<MessageHtmlElement[]>>

  updateMessageFeedback: (
    sessionId: string,
    modelId: string,
    providerId: string,
    messageId: string,
    thumbsUp: boolean
  ) => Promise<void>

  selectedMessages: string[]
  isSelecting: boolean
  handleMessageSelect: (messageId: string) => void
  setSelectedMessages: Dispatch<SetStateAction<string[]>>
  setIsSelecting: Dispatch<SetStateAction<boolean>>

  isNewChat: boolean
  setIsNewChat: (isNewChat: boolean) => void

  applyChatSummary: (summary: string) => void
  activeChatKey: string
  getChatStoreKey: (chatId?: string | null) => string
  getScopedChatBindings: (chatKey: string) => ScopedChatBindings
  migrateChatState: (fromChatKey: string, toChatKey: string) => void
  setActiveChatOverrideId: Dispatch<SetStateAction<string | null>>
  pendingNavigationChatId: string | null
  setPendingNavigationChatId: Dispatch<SetStateAction<string | null>>
}

export const ChatbotUIChatContext = createContext<ChatbotUIChatContext>({
  // PASSIVE CHAT STORE
  chatMessages: [],
  setChatMessages: () => {},
  chatSettings: null,
  setChatSettings: () => {},
  selectedChat: null,
  setSelectedChat: () => {},
  chatFileItems: [],
  setChatFileItems: () => {},

  // Chat input
  hasScopedInputState: false,
  inputScopeId: 'global',
  userInput: '',
  setUserInput: () => {},

  // ACTIVE CHAT STORE
  isGenerating: false,
  setIsGenerating: () => {},
  isPersisting: false,
  setIsPersisting: () => {},
  isImageGenerating: false,
  setIsImageGenerating: () => {},
  isMusicGenerating: false,
  setIsMusicGenerating: () => {},
  isVideoGenerating: false,
  setIsVideoGenerating: () => {},
  firstTokenReceived: false,
  setFirstTokenReceived: () => {},
  abortController: null,
  setAbortController: () => {},

  responseTimeToFirstToken: 0,
  setResponseTimeToFirstToken: () => {},
  responseTimeTotal: 0,
  setResponseTimeTotal: () => {},
  responseTokensTotal: 0,
  setResponseTokensTotal: () => {},
  requestTokensTotal: 0,
  setRequestTokensTotal: () => {},

  selectedTools: [],
  setSelectedTools: () => {},
  getSelectedToolsSnapshot: () => [],

  selectedHtmlElements: [],
  setSelectedHtmlElements: () => {},

  updateMessageFeedback: (
    sessionId: string,
    modelId: string,
    providerId: string,
    messageId: string,
    thumbsUp: boolean
  ) => Promise.resolve(),
  setSelectedMessages: () => {},
  setIsSelecting: () => {},
  selectedMessages: [],
  isSelecting: false,
  handleMessageSelect: () => {},

  isNewChat: false,
  setIsNewChat: () => {},

  applyChatSummary: () => {},
  activeChatKey: 'draft',
  getChatStoreKey: () => 'draft',
  getScopedChatBindings: () => ({
    getSnapshot: () => createEmptyChatScopedState(),
    setChatMessages: () => {},
    setChatFileItems: () => {},
    setAbortController: () => {},
    setFirstTokenReceived: () => {},
    setIsGenerating: () => {},
    setIsPersisting: () => {},
    setRequestTokensTotal: () => {},
    setResponseTimeToFirstToken: () => {},
    setResponseTimeTotal: () => {},
    setResponseTokensTotal: () => {}
  }),
  migrateChatState: () => {},
  setActiveChatOverrideId: () => {},
  pendingNavigationChatId: null,
  setPendingNavigationChatId: () => {}
})

interface ChatbotUIChatProviderProps {
  id: string
  children: React.ReactNode
  routeScoped?: boolean
}

export const ChatbotUIChatProvider: FC<ChatbotUIChatProviderProps> = ({
  id,
  children,
  routeScoped = true
}) => {
  const pathname = usePathname()
  useRootMountMarker('chat-provider')
  const [chatSettings, setChatSettings] = useState<ChatSettings>(() => {
    // Try to get existing settings from localStorage
    const savedSettings =
      typeof window !== 'undefined' ? localStorage.getItem('chatSettings') : null
    if (savedSettings) {
      try {
        return JSON.parse(savedSettings)
      } catch (e) {
        console.error('Failed to parse saved chat settings:', e)
      }
    }

    return {
      model: 'losi-omega',
      prompt: 'You are a helpful AI assistant.',
      temperature: 0.5,
      contextLength: 100000,
      includeProfileContext: true,
      includeWorkspaceInstructions: true,
      embeddingsProvider: 'openai',
      reflectMode: false
    }
  })

  // Save settings to localStorage when they change
  useEffect(() => {
    if (typeof window !== 'undefined') {
      localStorage.setItem('chatSettings', JSON.stringify(chatSettings))
    }
  }, [chatSettings])

  const applyChatSummary = useCallback(
    (summary: string) => {
      if (summary && chatSettings) {
        const contextualPrompt = `Previous conversation summary:\n${summary}\n\nContinuing from this context, please respond helpfully and acknowledge the previous conversation when relevant.`

        const newSettings = {
          ...chatSettings,
          prompt: chatSettings.prompt
            ? `${chatSettings.prompt}\n\n${contextualPrompt}`
            : contextualPrompt
        }

        // Persist synchronously to localStorage BEFORE updating state
        // to win the race against Strict Mode remounting.
        localStorage.setItem('chatSettings', JSON.stringify(newSettings))
        setChatSettings(newSettings)
      }
    },
    [chatSettings]
  )

  const updateMessageFeedback = async (
    sessionId: string,
    modelId: string,
    providerId: string,
    messageId: string,
    thumbsUp: boolean
  ) => {
    try {
      await updateMessageFeedbackInDB(sessionId, modelId, providerId, messageId, thumbsUp)
      setChatMessages((prevMessages) =>
        prevMessages.map((msg) =>
          msg.message.id === messageId
            ? { ...msg, message: { ...msg.message, thumbs_up: thumbsUp } }
            : msg
        )
      )
    } catch (error) {
      console.error('Error updating message feedback:', error)
      // You might want to show an error toast here
    }
  }

  const [selectedChat, setSelectedChat] = useState<Tables<'chats'> | null>(null)
  const [activeChatOverrideId, setActiveChatOverrideId] = useState<string | null>(null)
  const [pendingNavigationChatId, setPendingNavigationChatId] = useState<string | null>(null)
  const draftChatKey = `draft:${id}`
  const getChatStoreKey = useCallback(
    (chatId?: string | null) => {
      const trimmedChatId = typeof chatId === 'string' ? chatId.trim() : ''
      return trimmedChatId || draftChatKey
    },
    [draftChatKey]
  )
  const routeChatId =
    routeScoped && pathname?.startsWith('/chat/')
      ? pathname.split('/').filter(Boolean)[1] || null
      : routeScoped && pathname === '/chat'
        ? null
        : undefined
  const activeChatId =
    routeScoped && pathname?.startsWith('/chat/')
      ? routeChatId
      : routeScoped && pathname === '/chat'
        ? pendingNavigationChatId
        : activeChatOverrideId || selectedChat?.id || null
  const activeChatKey = getChatStoreKey(activeChatId)
  const [chatStateByKey, setChatStateByKey] = useState<Record<string, ChatScopedState>>(() => ({
    [draftChatKey]: createEmptyChatScopedState()
  }))
  const chatStateRef = useRef<Record<string, ChatScopedState>>(chatStateByKey)
  const commitChatStateByKey = useCallback((nextStateByKey: Record<string, ChatScopedState>) => {
    chatStateRef.current = nextStateByKey
    return nextStateByKey
  }, [])

  useEffect(() => {
    chatStateRef.current = chatStateByKey
  }, [chatStateByKey])

  useEffect(() => {
    if (!pendingNavigationChatId || pathname === '/chat') {
      return
    }

    setPendingNavigationChatId(null)
  }, [pathname, pendingNavigationChatId])

  useEffect(() => {
    setChatStateByKey((previous) => {
      if (previous[activeChatKey]) {
        return previous
      }

      return commitChatStateByKey({
        ...previous,
        [activeChatKey]: createEmptyChatScopedState()
      })
    })
  }, [activeChatKey, commitChatStateByKey])

  const getChatStateSnapshot = useCallback((chatKey: string): ChatScopedState => {
    return chatStateRef.current[chatKey] ?? createEmptyChatScopedState()
  }, [])

  const updateScopedChatState = useCallback(
    <Key extends keyof ChatScopedState>(
      chatKey: string,
      key: Key,
      value: SetStateAction<ChatScopedState[Key]>
    ) => {
      setChatStateByKey((previous) => {
        const current = previous[chatKey] ?? createEmptyChatScopedState()
        const nextValue = resolveStateAction(value, current[key])

        if (Object.is(current[key], nextValue)) {
          return previous
        }

        // DIAGNOSTIC: the "collapses to loader mid-stream" bug is a random race
        // where firstTokenReceived is downgraded true->false while a stream
        // still owns the turn (abortController set). Capture the culprit call
        // path so the next random occurrence self-identifies which code did it.
        if (
          key === 'firstTokenReceived' &&
          current.firstTokenReceived === true &&
          nextValue === false &&
          current.abortController
        ) {
          safeConsole.warn(
            '[stream-collapse] firstTokenReceived downgraded true->false during active stream',
            { chatKey, stack: new Error().stack }
          )
        }

        return commitChatStateByKey({
          ...previous,
          [chatKey]: {
            ...current,
            [key]: nextValue
          }
        })
      })
    },
    [commitChatStateByKey]
  )

  const getScopedChatBindings = useCallback(
    (chatKey: string): ScopedChatBindings => ({
      getSnapshot: () => getChatStateSnapshot(chatKey),
      setChatMessages: (value) =>
        updateScopedChatState(chatKey, 'chatMessages', value),
      setChatFileItems: (value) =>
        updateScopedChatState(chatKey, 'chatFileItems', value),
      setAbortController: (value) =>
        updateScopedChatState(chatKey, 'abortController', value),
      setFirstTokenReceived: (value) =>
        updateScopedChatState(chatKey, 'firstTokenReceived', value),
      setIsGenerating: (value) =>
        updateScopedChatState(chatKey, 'isGenerating', value),
      setIsPersisting: (value) =>
        updateScopedChatState(chatKey, 'isPersisting', value),
      setRequestTokensTotal: (value) =>
        updateScopedChatState(chatKey, 'requestTokensTotal', value),
      setResponseTimeToFirstToken: (value) =>
        updateScopedChatState(chatKey, 'responseTimeToFirstToken', value),
      setResponseTimeTotal: (value) =>
        updateScopedChatState(chatKey, 'responseTimeTotal', value),
      setResponseTokensTotal: (value) =>
        updateScopedChatState(chatKey, 'responseTokensTotal', value)
    }),
    [getChatStateSnapshot, updateScopedChatState]
  )

  const migrateChatState = useCallback((fromChatKey: string, toChatKey: string) => {
    if (!fromChatKey || !toChatKey || fromChatKey === toChatKey) {
      return
    }

    setChatStateByKey((previous) => {
      const fromState = previous[fromChatKey]
      if (!fromState) return previous

      const toState = previous[toChatKey] ?? createEmptyChatScopedState()
      const nextState = {
        ...toState,
        chatMessages:
          fromState.chatMessages.length > 0 ? fromState.chatMessages : toState.chatMessages,
        chatFileItems:
          fromState.chatFileItems.length > 0 ? fromState.chatFileItems : toState.chatFileItems,
        abortController: fromState.abortController,
        firstTokenReceived: fromState.firstTokenReceived,
        isGenerating: fromState.isGenerating,
        isPersisting: fromState.isPersisting,
        requestTokensTotal: Math.max(
          fromState.requestTokensTotal,
          toState.requestTokensTotal
        ),
        responseTimeToFirstToken: Math.max(
          fromState.responseTimeToFirstToken,
          toState.responseTimeToFirstToken
        ),
        responseTimeTotal: Math.max(fromState.responseTimeTotal, toState.responseTimeTotal),
        responseTokensTotal: Math.max(
          fromState.responseTokensTotal,
          toState.responseTokensTotal
        )
      }

      const updatedState = {
        ...previous,
        [toChatKey]: nextState
      }

      delete updatedState[fromChatKey]

      return commitChatStateByKey(updatedState)
    })
  }, [commitChatStateByKey])

  const activeChatState = getChatStateSnapshot(activeChatKey)
  const chatMessages = activeChatState.chatMessages
  const setChatMessages = getScopedChatBindings(activeChatKey).setChatMessages
  const chatFileItems = activeChatState.chatFileItems
  const setChatFileItems = getScopedChatBindings(activeChatKey).setChatFileItems

  // ACTIVE CHAT STORE
  const isGenerating = activeChatState.isGenerating
  const isPersisting = activeChatState.isPersisting
  const [isImageGenerating, setIsImageGenerating] = useState<boolean>(false)
  const [isMusicGenerating, setIsMusicGenerating] = useState<boolean>(false)
  const [isVideoGenerating, setIsVideoGenerating] = useState<boolean>(false)
  const firstTokenReceived = activeChatState.firstTokenReceived
  const abortController = activeChatState.abortController

  const setIsGenerating = getScopedChatBindings(activeChatKey).setIsGenerating
  const setIsPersisting = getScopedChatBindings(activeChatKey).setIsPersisting
  const setFirstTokenReceived =
    getScopedChatBindings(activeChatKey).setFirstTokenReceived
  const setAbortController = getScopedChatBindings(activeChatKey).setAbortController

  const responseTimeToFirstToken = activeChatState.responseTimeToFirstToken
  const responseTimeTotal = activeChatState.responseTimeTotal
  const responseTokensTotal = activeChatState.responseTokensTotal
  const requestTokensTotal = activeChatState.requestTokensTotal
  const setResponseTimeToFirstToken =
    getScopedChatBindings(activeChatKey).setResponseTimeToFirstToken
  const setResponseTimeTotal = getScopedChatBindings(activeChatKey).setResponseTimeTotal
  const setResponseTokensTotal =
    getScopedChatBindings(activeChatKey).setResponseTokensTotal
  const setRequestTokensTotal = getScopedChatBindings(activeChatKey).setRequestTokensTotal

  // Persist tool toggles across Crew Chat ↔ thread navigations via jotai
  // storage (same atom as useTools). Ephemeral useState reset tools on remount.
  const [selectedTools, setSelectedToolsAtom] = useAtom(selectedToolsAtom)
  const selectedToolsRef = useRef<Tables<'tools'>[]>(selectedTools)
  const setSelectedTools = useCallback<Dispatch<SetStateAction<Tables<'tools'>[]>>>(
    (value) => {
      setSelectedToolsAtom((previous) => {
        const next = resolveStateAction(value, previous)
        selectedToolsRef.current = next
        return next
      })
    },
    [setSelectedToolsAtom]
  )
  const getSelectedToolsSnapshot = useCallback(() => selectedToolsRef.current, [])

  useEffect(() => {
    selectedToolsRef.current = selectedTools
  }, [selectedTools])

  const [selectedHtmlElements, setSelectedHtmlElements] = useState<MessageHtmlElement[]>([])

  const [selectedMessages, setSelectedMessages] = useState<string[]>([])
  const [isSelecting, setIsSelecting] = useState(false)

  const [isNewChat, setIsNewChat] = useState(false)

  const handleMessageSelect = (messageId: string) => {
    setSelectedMessages((prev) =>
      prev.includes(messageId) ? prev.filter((id) => id !== messageId) : [...prev, messageId]
    )
  }

  useEffect(() => {
    const handleImageGenerationStarted = () => {
      setIsImageGenerating(true)
    }

    const handleImageGenerationEnded = () => {
      setIsImageGenerating(false)
    }

    const handleMusicGenerationStarted = () => {
      setIsMusicGenerating(true)
    }

    const handleMusicGenerationEnded = () => {
      setIsMusicGenerating(false)
    }

    const handleVideoGenerationStarted = () => {
      setIsVideoGenerating(true)
    }

    const handleVideoGenerationEnded = () => {
      setIsVideoGenerating(false)
    }

    Emitter.on('imageGenerationStarted', handleImageGenerationStarted)
    Emitter.on('imageGenerationEnded', handleImageGenerationEnded)
    Emitter.on('musicGenerationStarted', handleMusicGenerationStarted)
    Emitter.on('musicGenerationEnded', handleMusicGenerationEnded)
    Emitter.on('videoGenerationStarted', handleVideoGenerationStarted)
    Emitter.on('videoGenerationEnded', handleVideoGenerationEnded)

    return () => {
      Emitter.off('imageGenerationStarted', handleImageGenerationStarted)
      Emitter.off('imageGenerationEnded', handleImageGenerationEnded)
      Emitter.off('musicGenerationStarted', handleMusicGenerationStarted)
      Emitter.off('musicGenerationEnded', handleMusicGenerationEnded)
      Emitter.off('videoGenerationStarted', handleVideoGenerationStarted)
      Emitter.off('videoGenerationEnded', handleVideoGenerationEnded)
    }
  }, [])

  return (
    <ChatbotUIChatContext.Provider
      value={{
        // PASSIVE CHAT STORE
        chatMessages,
        setChatMessages,
        chatSettings,
        setChatSettings,
        selectedChat,
        setSelectedChat,
        chatFileItems,
        setChatFileItems,

        // Chat input
        hasScopedInputState: true,
        inputScopeId: id,
        userInput: '',
        setUserInput: ignoreUserInputUpdate,

        // ACTIVE CHAT STORE
        isGenerating,
        setIsGenerating,
        isPersisting,
        setIsPersisting,
        isImageGenerating,
        setIsImageGenerating,
        isMusicGenerating,
        setIsMusicGenerating,
        isVideoGenerating,
        setIsVideoGenerating,
        firstTokenReceived,
        setFirstTokenReceived,
        abortController,
        setAbortController,

        responseTimeToFirstToken,
        setResponseTimeToFirstToken,
        responseTimeTotal,
        setResponseTimeTotal,
        responseTokensTotal,
        setResponseTokensTotal,
        requestTokensTotal,
        setRequestTokensTotal,

        selectedTools,
        setSelectedTools,
        getSelectedToolsSnapshot,

        selectedHtmlElements,
        setSelectedHtmlElements,
        updateMessageFeedback,
        setSelectedMessages,
        setIsSelecting,
        selectedMessages,
        isSelecting,
        handleMessageSelect,

        isNewChat,
        setIsNewChat,
        applyChatSummary,
        activeChatKey,
        getChatStoreKey,
        getScopedChatBindings,
        migrateChatState,
        setActiveChatOverrideId,
        pendingNavigationChatId,
        setPendingNavigationChatId
      }}
    >
      <ScopedChatInputStateProvider inputScopeId={activeChatKey}>
        {children}
      </ScopedChatInputStateProvider>
    </ChatbotUIChatContext.Provider>
  )
}
